import React, { useState } from 'react'

import { Menu, Popover, TextInput, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { AlertTriangle } from 'lucide-react'

import { useMutation, useQuery } from '@redwoodjs/web'

dayjs.extend(utc)

import DateShortcutButton from 'src/components/Inputs/DateShortcutButton/DateShortcutButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_APPLICATION_BY_APPLICANT_ID } from 'src/graphql/application.graphql'
import {
  GET_DOCUMENT_INTELLIGENCE,
  UPDATE_DOCUMENT,
} from 'src/graphql/documents.graphql'
import { GET_STEP_OPTIONS } from 'src/graphql/steps.graphql'
import { DocumentUserStatus } from 'src/graphql/types/documents'
import { DQF_STEP_TYPES, StepType } from 'src/graphql/types/steps'
import useVoEDocumentCompletionGate from 'src/hooks/useVoEDocumentCompletionGate/useVoEDocumentCompletionGate'
import type { DocumentVoELink } from 'src/hooks/useVoEDocumentCompletionGate/useVoEDocumentCompletionGate'
import IconSearch from 'src/icons/IconSearch'
import IconSparkle from 'src/icons/IconSparkle'
import { buildDocumentCategoryOptions } from 'src/lib/documentCategory.utils'
import {
  buildActualValues,
  formatExtractedRowValue,
  valuesMismatch,
} from 'src/lib/documentIntelligence'
import {
  formatSnakeValue,
  formatDocumentCategory,
  getStepLabel,
} from 'src/lib/formatters'
import { sortStepsByTemplate } from 'src/lib/stepOrder'
import { cn } from 'src/utils'
import { isSameUtcDay } from 'src/utils/date/toMonthYearKey'

import { AiConfidencePill } from '../AiConfidencePill'
import ExtractedFieldsPanel from '../ExtractedFieldsPanel/ExtractedFieldsPanel'

type ChipState = 'accepted' | 'pending'

// Returns the chip state for a given prediction field, factoring in user review actions.
// Returns null when the chip should not be shown.
function getChipState(
  field: string,
  pred:
    | {
        autoApplied: boolean
        overriddenAfterAutoApply?: boolean | null
        confidence?: number | null
        expirationValue?: string | null
        categoryValue?: string | null
        reviewDecision?: string | null
      }
    | undefined,
  document: { expirationDate?: string | null; category?: string | null } | null,
  appliedFields: Set<string>,
  dismissedFields: Set<string>,
  aiCompleted: boolean
): ChipState | null {
  // No prediction for this field. Sub-threshold predictions are dropped by the
  // backend, so if AI ran (COMPLETED) the absence means AI wasn't confident —
  // surface a grey "AI unsure" chip so the recruiter sees AI looked. If AI
  // never ran, there's nothing to show.
  if (!pred) {
    if (!aiCompleted) return null
    // Only flag "AI unsure" when the field isn't already satisfied. CATEGORY is
    // preset/locked from the upload context (VoE, DQF, hiring step) so the
    // document already carries it; an expiration the recruiter already entered
    // shouldn't be second-guessed either. In both cases AI was never the source
    // of record, so the marker would mislead. Mirrors the upload modal's
    // `!form.values.{category,expirationDate}` guards.
    if (field === 'CATEGORY' && document?.category) return null
    if (field === 'DATE' && document?.expirationDate) return null
    return 'pending'
  }
  // No value was extracted — chip has nothing to show
  if (field === 'DATE' && !pred.expirationValue) return null
  if (field === 'CATEGORY' && !pred.categoryValue) return null
  // User dismissed this field without applying in the current session
  if (dismissedFields.has(field) && !appliedFields.has(field)) return null
  // Backend says this prediction was explicitly rejected in a prior session
  if (pred.reviewDecision === 'KEPT_ORIGINAL') return null
  // Recruiter edited the field after AI auto-applied it — the AI value is no
  // longer the source of truth, so the "AI · XX%" pill would mislead. The
  // popover's per-field pill still shows the rejected/applied history.
  if (pred.overriddenAfterAutoApply) return null

  // Check whether the document's current value matches the AI suggestion.
  // Dates are compared on the UTC calendar day via isSameUtcDay — local-time
  // formatting (e.g. `toDateString()`) can flip the day across timezone
  // boundaries and incorrectly mark a matching prediction as "conflicting".
  const valueMatchesAI =
    field === 'DATE' && pred.expirationValue && document?.expirationDate
      ? isSameUtcDay(pred.expirationValue, document.expirationDate)
      : field === 'CATEGORY' && pred.categoryValue && document?.category
      ? pred.categoryValue === document.category
      : false

  // If the document already has a different value than the AI suggestion, the
  // suggestion is irrelevant — recruiter already made a different choice. Hide the chip.
  const hasConflictingValue =
    field === 'DATE'
      ? !!(pred.expirationValue && document?.expirationDate && !valueMatchesAI)
      : field === 'CATEGORY'
      ? !!(pred.categoryValue && document?.category && !valueMatchesAI)
      : false
  if (hasConflictingValue) return null

  if (
    pred.autoApplied ||
    pred.reviewDecision === 'ACCEPTED_AI' ||
    appliedFields.has(field) ||
    valueMatchesAI
  )
    return 'accepted'
  // A confident prediction with no applied value yet: the side-panel action
  // card is the affordance, so no chip here. (Low-confidence predictions never
  // reach this point — they're filtered on the backend and handled by the
  // `!pred` branch above as the grey "AI unsure" chip.)
  return null
}

// Inline AI badge next to a field value.
//  - accepted: blue "✦ AI · XX%" pill; tooltip carries the confidence bar.
//  - pending (low confidence): grey "✦ AI" marker with NO percentage. The number
//    is omitted on purpose — with no value applied and no action card, "AI · 72%"
//    reads as a broken promise ("72% sure of what?"). The tooltip explains it.
const AiChip = React.forwardRef<
  HTMLSpanElement,
  {
    state: ChipState
    confidence: number
  } & React.HTMLAttributes<HTMLSpanElement>
>(({ state, confidence, ...props }, ref) => {
  const isAccepted = state === 'accepted'

  if (!isAccepted) {
    return (
      <Tooltip label="AI unsure — no suggestion" withArrow className="text-xs">
        <span
          ref={ref}
          aria-label="AI reviewed, no confident suggestion"
          className="inline-flex items-center gap-0.5 rounded-full bg-doubleNickel-gray-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-doubleNickel-gray-500"
          {...props}
        >
          <IconSparkle size={9} />
          AI
        </span>
      </Tooltip>
    )
  }

  return (
    <AiConfidencePill
      ref={ref}
      confidence={confidence}
      tooltipLabel="AI predicted · accepted"
      {...props}
    />
  )
})

import DateInput from './DateInput'

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[10px] font-semibold uppercase tracking-wide text-doubleNickel-gray-400">
    {children}
  </span>
)

const categories = buildDocumentCategoryOptions(formatDocumentCategory)

const onError = () => {
  toast('Unable to update document, please try again.', 'error')
}

const DocumentEditScreen = ({
  document,
  documentId,
  onRender,
  documentIntelligence = null,
  appliedFields = new Set<string>(),
  dismissedFields = new Set<string>(),
  applicantId,
  voe = null,
  voeLinkPending = false,
  onVoELinkPending,
}: {
  document: any
  documentId: string
  onRender: any[]
  documentIntelligence?: any
  appliedFields?: Set<string>
  dismissedFields?: Set<string>
  applicantId?: string
  /** The verification this document is linked to, if any. */
  voe?: DocumentVoELink | null
  /** True while the caller is still loading (or failed to load) `voe`. */
  voeLinkPending?: boolean
  onVoELinkPending?: () => void
}) => {
  const [search, setSearch] = useState('')
  const [isExpirationDateMenuOpen, setIsExpirationDateMenuOpen] =
    useState(false)
  // Controlled like MvrFindingsPill so clicks/scrolls inside the document
  // preview don't dismiss the popover before the recruiter can scroll it.
  const [extractedOpened, { toggle: toggleExtracted, close: closeExtracted }] =
    useDisclosure(false)

  // Find completed AI predictions for the two editable fields
  const completedPredictions =
    documentIntelligence?.status === 'COMPLETED'
      ? documentIntelligence.predictions
      : []
  const expirationPred = completedPredictions.find((p) => p.field === 'DATE')
  const categoryPred = completedPredictions.find((p) => p.field === 'CATEGORY')

  // Read-only rows for the "AI Extracted" popover (License #, State, MVR
  // accident/violation counts, …). Assembled on the backend — one flat,
  // threshold-filtered, display-ready list combining scalar extractions and
  // finding-type count rows (with per-record hover details).
  const extractedFields = documentIntelligence?.aiExtractedRows ?? []

  // The value of record for each field, shown in each pill's tooltip so the user
  // can compare the AI extraction against what's actually in the application.
  // Core fields come from the document; license details from the application's
  // current license (fetched by applicantId — cached when the Application tab is
  // already open). Only fetched when there are extracted fields to compare.
  const { data: applicationData } = useQuery(GET_APPLICATION_BY_APPLICANT_ID, {
    variables: { applicantId },
    skip: !applicantId || extractedFields.length === 0,
    fetchPolicy: 'cache-first',
  })
  const license = applicationData?.applicationByApplicantId?.license
  const actualValues = buildActualValues({ document, license })

  // Checklist-item options for the "Checklist" menu — active steps for this
  // applicant, in template order, deduped by type/custom step.
  const { data: stepOptionsData } = useQuery(GET_STEP_OPTIONS, {
    variables: {
      applicantId,
      stepTypes: [...DQF_STEP_TYPES, 'CUSTOM' as StepType],
      pageInfo: { offset: 0, limit: 100 },
    },
    skip: !applicantId,
  })
  const stepOptions = sortStepsByTemplate(
    stepOptionsData?.stepsGroupByType?.items ?? [],
    null
  )
  const currentStep = stepOptions.find(
    (step) => step.stepId === document.stepId
  )

  // Any extracted field whose AI value differs from the application's value of
  // record — surfaced as an amber cue on the popover trigger so the user knows
  // to open it.
  const hasMismatch = extractedFields.some((f) =>
    valuesMismatch(
      f.field,
      formatExtractedRowValue(f.field, f.value),
      actualValues[f.field]
    )
  )

  const aiCompleted = documentIntelligence?.status === 'COMPLETED'
  const expirationChipState = getChipState(
    'DATE',
    expirationPred,
    document,
    appliedFields,
    dismissedFields,
    aiCompleted
  )
  const categoryChipState = getChipState(
    'CATEGORY',
    categoryPred,
    document,
    appliedFields,
    dismissedFields,
    aiCompleted
  )

  // Always refetch GET_DOCUMENT_INTELLIGENCE on update — the backend flips
  // DocumentPrediction.overriddenAfterAutoApply when a recruiter edits a field
  // that AI had auto-applied. Without this refetch, Apollo serves stale
  // predictions and the chip-hiding guard in getChipState never fires.
  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    refetchQueries: [...onRender, GET_DOCUMENT_INTELLIGENCE],
    onCompleted: () => {
      toast('Document updated successfully', 'success')
    },
    onError,
  })

  const { requestStatusChange, modal: voeCompletionModal } =
    useVoEDocumentCompletionGate()

  const handleStatusChange = (status: DocumentUserStatus) =>
    requestStatusChange({
      voe,
      userStatus: status,
      currentStatus: document.userStatus,
      voeLinkPending,
      onVoELinkPending,
      send: (voeCompletion) =>
        updateDocument({
          variables: {
            id: documentId,
            input: {
              userStatus: status,
              ...(voeCompletion && { voeCompletion }),
            },
          },
        }),
    })

  const handleDateChange = (date) => {
    updateDocument({
      variables: { id: documentId, input: { expirationDate: date } },
    })
    setIsExpirationDateMenuOpen(false)
  }

  const menuClassNames = {
    dropdown: 'rounded shadow-sm p-1',
    item: 'text-doubleNickel-gray-700 text-xs p-2',
  }

  return (
    // Wraps to a second line when narrow — each field group stays intact
    // (shrink-0 + nowrap) instead of squeezing into truncated fragments.
    <div className="flex flex-row flex-wrap items-center gap-x-6 gap-y-2 text-xs">
      {/* Expiration date */}
      <div className="flex shrink-0 items-center gap-1.5">
        <FieldLabel>Expiration</FieldLabel>
        <Menu
          offset={6}
          position="bottom-start"
          opened={isExpirationDateMenuOpen}
          onOpen={() => setIsExpirationDateMenuOpen(true)}
          onClose={() => setIsExpirationDateMenuOpen(false)}
          classNames={{
            ...menuClassNames,
            dropdown: 'rounded shadow-sm p-1 max-h-[380px] overflow-y-auto',
          }}
        >
          <Menu.Target>
            <button
              data-testid="expiration-date-menu-trigger"
              className="whitespace-nowrap text-doubleNickel-gray-700 hover:text-doubleNickel-gray-900 hover:underline"
            >
              {document.expirationDate
                ? dayjs.utc(document.expirationDate).format('MM/DD/YYYY')
                : 'Set date'}
            </button>
          </Menu.Target>
          <Menu.Dropdown className="p-2">
            <DateInput
              initialValue={
                document.expirationDate
                  ? (() => {
                      const utc = new Date(document.expirationDate)
                      return new Date(
                        utc.getUTCFullYear(),
                        utc.getUTCMonth(),
                        utc.getUTCDate()
                      )
                    })()
                  : null
              }
              onDateChange={handleDateChange}
              size="xs"
            />
            <DateShortcutButton
              onClick={(_event, date) => handleDateChange(date)}
            />
          </Menu.Dropdown>
        </Menu>
        {expirationChipState && (
          <span className="shrink-0">
            <AiChip
              state={expirationChipState}
              confidence={expirationPred?.confidence ?? 0}
            />
          </span>
        )}
      </div>

      {/* Category */}
      <div className="flex shrink-0 items-center gap-1.5">
        <FieldLabel>Category</FieldLabel>
        <Menu
          offset={6}
          width={225}
          position="bottom-start"
          classNames={menuClassNames}
        >
          <Menu.Target>
            <button
              data-testid="category-menu-trigger"
              className="max-w-[180px] truncate text-doubleNickel-gray-700 hover:text-doubleNickel-gray-900 hover:underline"
            >
              {formatDocumentCategory(document.category)}
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <TextInput
              className="mb-1"
              placeholder="Search for a category"
              leftSection={
                <IconSearch className="h-3 w-3 fill-none stroke-doubleNickel-gray-700" />
              }
              size="xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-[250px] overflow-y-auto">
              {categories
                .filter((category) =>
                  category.label.toLowerCase().includes(search.toLowerCase())
                )
                .map((category) => (
                  <Menu.Item
                    key={category.value}
                    onClick={() =>
                      updateDocument({
                        variables: {
                          id: documentId,
                          input: { category: category.value },
                        },
                      })
                    }
                  >
                    {category.label}
                  </Menu.Item>
                ))}
            </div>
          </Menu.Dropdown>
        </Menu>
        {categoryChipState && (
          <span className="shrink-0">
            <AiChip
              state={categoryChipState}
              confidence={categoryPred?.confidence ?? 0}
            />
          </span>
        )}
      </div>

      {document.createdAt && (
        <div className="flex shrink-0 items-center gap-1.5">
          <FieldLabel>Uploaded</FieldLabel>
          <span className="whitespace-nowrap text-doubleNickel-gray-700">
            {dayjs.utc(document.createdAt).format('MM/DD/YYYY')}
          </span>
        </div>
      )}

      {/* Status */}
      <div className="flex shrink-0 items-center gap-1.5">
        <FieldLabel>Status</FieldLabel>
        <Menu
          offset={6}
          width={185}
          position="bottom-start"
          classNames={menuClassNames}
        >
          <Menu.Target>
            <button
              data-testid="status-menu-trigger"
              className={cn(
                'whitespace-nowrap font-medium hover:underline',
                document.userStatus === DocumentUserStatus.COMPLETED
                  ? 'text-doubleNickel-success-600'
                  : 'text-doubleNickel-warning-600'
              )}
            >
              {formatSnakeValue(document.userStatus)}
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            {Object.values(DocumentUserStatus).map((status) => (
              <Menu.Item
                key={status}
                onClick={() => handleStatusChange(status)}
              >
                {formatSnakeValue(status)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </div>

      {/* Checklist item — explicit step assignment (or None to detach). */}
      {applicantId && (
        <div className="flex shrink-0 items-center gap-1.5">
          <FieldLabel>Checklist</FieldLabel>
          <Menu
            offset={6}
            width={225}
            position="bottom-start"
            classNames={menuClassNames}
          >
            <Menu.Target>
              <button
                data-testid="checklist-step-menu-trigger"
                className="max-w-[180px] truncate text-doubleNickel-gray-700 hover:text-doubleNickel-gray-900 hover:underline"
              >
                {currentStep ? getStepLabel(currentStep) : 'None'}
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              <div className="max-h-[250px] overflow-y-auto">
                <Menu.Item
                  onClick={() => {
                    if (!document.stepId) return
                    updateDocument({
                      variables: { id: documentId, input: { stepId: null } },
                    })
                  }}
                >
                  None
                </Menu.Item>
                {stepOptions.map((step) => (
                  <Menu.Item
                    key={step.stepId}
                    onClick={() => {
                      if (step.stepId === document.stepId) return
                      updateDocument({
                        variables: {
                          id: documentId,
                          input: { stepId: step.stepId },
                        },
                      })
                    }}
                  >
                    {getStepLabel(step)}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Dropdown>
          </Menu>
        </div>
      )}

      {/* Read-only "Extracted by AI" popover — only for docs with extra extracted
          fields (e.g. licenses). Pushed to the right; document stays the focus. */}
      {extractedFields.length > 0 && (
        <Popover
          position="bottom-end"
          offset={6}
          shadow="md"
          width={340}
          opened={extractedOpened}
          onDismiss={closeExtracted}
        >
          <Popover.Target>
            <button
              type="button"
              data-testid="extracted-fields-trigger"
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-doubleNickel-brand-100 bg-doubleNickel-brand-25 px-2.5 py-1 text-[11px] font-semibold text-doubleNickel-brand-500"
              onClick={(e) => {
                e.stopPropagation()
                toggleExtracted()
              }}
            >
              <IconSparkle size={11} />
              AI Extracted {extractedFields.length}
              {hasMismatch && (
                <AlertTriangle
                  size={11}
                  className="text-doubleNickel-warning-500"
                />
              )}
            </button>
          </Popover.Target>
          <Popover.Dropdown
            className="p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <ExtractedFieldsPanel
              fields={extractedFields}
              actualValues={actualValues}
            />
          </Popover.Dropdown>
        </Popover>
      )}

      {voeCompletionModal}
    </div>
  )
}

export default DocumentEditScreen
